import { getPpnuid } from '@porsche-code/kado-devtools';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useEffect, useRef } from 'react';

import { getPpnSlug } from './helpers';

type AUTH_EVENT =
  | 'signInWithRedirect'
  | 'signInWithRedirect_failure'
  | 'tokenRefresh'
  | 'tokenRefresh_failure'
  | 'customOAuthState'
  | 'signedIn'
  | 'signedOut';

const extractPpnuid = (username: string): string =>
  username.replace(/^ppn(lite)?_/, '');

const isPasswordFlowActive = () =>
  !Amplify.getConfig().Auth?.Cognito.loginWith?.oauth;

const removeCodeParameterFromUrl = (): void => {
  const currentUrl = new URL(window.location.href);

  if (currentUrl.searchParams.has('code')) {
    currentUrl.searchParams.delete('code');
    window.history.replaceState(null, '', currentUrl.toString());
  }
};

export const useAuth = (
  onSuccess: (
    user: { ppnuid: string } | { token: string; anonymousUserId: string },
    event?: AUTH_EVENT,
  ) => void,
  onFailure: (error: unknown) => void,
) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isRedirectingRef = useRef(false);

  const handleAuth = async (event?: AUTH_EVENT): Promise<void> => {
    const signedInUser = await getCurrentUser();
    const ppnuid = extractPpnuid(signedInUser.username);

    if (!ppnuid) {
      onFailure(new Error('Invalid username format - no PPNUID found'));
      return;
    }

    removeCodeParameterFromUrl();

    onSuccess({ ppnuid }, event);
  };

  const initiateRedirect = async (): Promise<void> => {
    if (isRedirectingRef.current) return;

    if (isPasswordFlowActive()) {
      onFailure(new Error('User needs to be authenticated'));
      return;
    }

    try {
      isRedirectingRef.current = true;
      const ppnSlug = getPpnSlug();

      // Store the current path (including search and hash) to redirect back after login
      const intendedPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      await signInWithRedirect({
        provider: { custom: ppnSlug },
        customState: intendedPath,
      });
    } catch (error) {
      isRedirectingRef.current = false;
      onFailure(error);
    }
  };

  useEffect(() => {
    const checkUserAuth = async (event?: AUTH_EVENT): Promise<void> => {
      if (isDevelopment) {
        const ppnuid = getPpnuid();

        if (ppnuid) {
          onSuccess({ ppnuid }, event);
          return;
        }
      }

      const urlParams = new URLSearchParams(window.location.search);

      if (urlParams.has('error') && urlParams.get('error') === 'server_error') {
        onFailure(new Error('SAML authentication failed'));
        return;
      }

      const authToken =
        sessionStorage.getItem('authToken') ?? urlParams.get('authToken');

      if (authToken && isNotExpired(authToken)) {
        // remove from search and store in session
        if (!sessionStorage.getItem('authToken')) {
          sessionStorage.setItem('authToken', authToken);
          sessionStorage.setItem('anonymousUserId', window.crypto.randomUUID());
          urlParams.delete('authToken');
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}?${urlParams}`,
          );
        }

        onSuccess(
          {
            token: authToken,
            anonymousUserId: sessionStorage.getItem('anonymousUserId')!,
          },
          event,
        );

        return;
      }

      try {
        await handleAuth(event);
      } catch {
        await initiateRedirect();
      }
    };

    checkUserAuth();

    const unsubscribeHub = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect_failure') {
        isRedirectingRef.current = false;
        onFailure(payload.data?.error || payload.data);
        return;
      }

      // Handle the custom OAuth state (RelayState) for redirecting to intended path
      if (payload.event === 'customOAuthState') {
        const intendedPath = payload.data;
        if (intendedPath && intendedPath !== '/') {
          // Store it temporarily to use after successful authentication
          sessionStorage.setItem('intendedPath', intendedPath);
        }
      }

      checkUserAuth(payload.event);
    });

    const handleAnonymousUserSignOut = () => {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('anonymousUserId');
      checkUserAuth();
    };
    window.addEventListener('anonymousUserSignOut', handleAnonymousUserSignOut);

    return () => {
      unsubscribeHub();
      window.removeEventListener(
        'anonymousUserSignOut',
        handleAnonymousUserSignOut,
      );
    };

    // Need to disable this rule beacuse the required fix causes infinite loops (a function values alway changes)
    // See: https://github.com/facebook/react/issues/14920#issuecomment-471070149
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const isNotExpired = (token: string) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = (payload.exp ?? 0) * 1000;
    return !exp || Date.now() < exp;
  } catch {
    return false;
  }
};

export const getIntendedPath = () => {
  const intendedPath = sessionStorage.getItem('intendedPath');

  if (intendedPath) {
    sessionStorage.removeItem('intendedPath');
  }

  return intendedPath;
};
